/* ============================================================
   Talention — Mock data + computation helpers
   Exposes window.TALENTION_DATA and helpers
   ============================================================ */

const FACTOR_GROUPS = [
  {
    id: "knowledge",
    name: "Conocimiento y Aptitudes",
    short: "Conocimiento",
    tint: "tint-knowledge",
    color: "#0a84ff",
    icon: "brain",
    iconBg: "#dfeeff",
    description: "Formación, conocimientos técnicos, idiomas y capacidades cognitivas requeridas",
    defaultSVPT: 30,
  },
  {
    id: "responsibility",
    name: "Responsabilidad",
    short: "Responsabilidad",
    tint: "tint-responsibility",
    color: "#af52de",
    icon: "shield",
    iconBg: "#f1e4fa",
    description: "Responsabilidad sobre personas, recursos, información o resultados",
    defaultSVPT: 35,
  },
  {
    id: "effort",
    name: "Esfuerzo",
    short: "Esfuerzo",
    tint: "tint-effort",
    color: "#ec7a15",
    icon: "zap",
    iconBg: "#ffe9d1",
    description: "Esfuerzo físico y mental requerido en el desempeño del puesto",
    defaultSVPT: 25,
  },
  {
    id: "conditions",
    name: "Condiciones de trabajo",
    short: "Condiciones",
    tint: "tint-conditions",
    color: "#34c759",
    icon: "factory",
    iconBg: "#e3f8e8",
    description: "Entorno, riesgos, horarios y condiciones físicas del puesto",
    defaultSVPT: 10,
  },
];

/* Items per group with levels (predefined valuations) */
const FACTOR_ITEMS = {
  knowledge: [
    {
      id: "k-formacion", name: "Formación académica", weight: 50, gender: "NEUTRO", selected: true,
      desc: "Nivel mínimo de enseñanza convencional para el puesto",
      levels: [
        { id: "L0", label: "No se requiere", desc: "No se requieren estudios específicos" },
        { id: "L1", label: "Primarios", desc: "Educación primaria / hasta 6º de EGB" },
        { id: "L2", label: "Secundarios", desc: "Educación secundaria obligatoria" },
        { id: "L3", label: "Bachillerato / FP", desc: "Bachillerato / 2º de FP de Grado II / COU" },
        { id: "L4", label: "Diplomatura / Grado", desc: "Diplomaturas / Ingenierías técnicas / Grado" },
        { id: "L5", label: "Licenciatura / Máster", desc: "Licenciaturas / Ingenierías / Máster" },
        { id: "L6", label: "Postgrado / Doctorado", desc: "Postgrado / Doctorado / MBA o similar" },
      ],
    },
    {
      id: "k-idiomas", name: "Conocimiento de idiomas", weight: 20, gender: "NEUTRO", selected: true,
      desc: "Nivel mínimo de idioma para el puesto",
      levels: [
        { id: "L0", label: "No se requiere", desc: "No es necesario el conocimiento de un idioma" },
        { id: "L1", label: "Bajo", desc: "Nivel básico (A2)" },
        { id: "L2", label: "Medio", desc: "Nivel intermedio (B1) o básico de más de uno" },
        { id: "L3", label: "Alto", desc: "Nivel alto (B2-C1) o intermedio de más de uno" },
        { id: "L4", label: "Muy alto", desc: "Nivel muy alto (C2) o alto de más de uno" },
      ],
    },
    {
      id: "k-liderazgo", name: "Capacidad de liderazgo", weight: 10, gender: "MASCULINO", selected: true,
      desc: "Capacidad para influir, motivar y organizar acciones",
      levels: [
        { id: "L0", label: "No se requiere", desc: "No requiere capacidad de liderazgo" },
        { id: "L1", label: "Bajo", desc: "Puntualmente, en proyectos ocasionales (<30%)" },
        { id: "L2", label: "Medio", desc: "Con frecuencia, mitad de la jornada" },
        { id: "L3", label: "Alto", desc: "Liderazgo permanente" },
      ],
    },
    {
      id: "k-iniciativa", name: "Iniciativa", weight: 5, gender: "NEUTRO", selected: true,
      desc: "Capacidad para planear, idear o emprender propuestas",
      levels: [
        { id: "L0", label: "No se requiere" },
        { id: "L1", label: "Baja" },
        { id: "L2", label: "Media" },
        { id: "L3", label: "Alta" },
      ],
    },
    {
      id: "k-maquinaria", name: "Manejo de maquinaria y/o equipos", weight: 15, gender: "NEUTRO", selected: true,
      desc: "Habilidad para operar equipos o maquinaria propia del puesto",
      levels: [
        { id: "L0", label: "No se requiere" },
        { id: "L1", label: "Básico" },
        { id: "L2", label: "Medio" },
        { id: "L3", label: "Avanzado" },
      ],
    },
    { id: "k-especificos", name: "Conocimientos específicos", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "k-dificultad", name: "Dificultad técnica", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "k-informaticos", name: "Conocimientos informáticos", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "k-sociales", name: "Habilidades sociales", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "k-creatividad", name: "Creatividad", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "k-comunicacion", name: "Habilidades de comunicación", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "k-fisicas", name: "Competencias físicas", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "k-destreza", name: "Habilidad manual-destreza", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "k-precision", name: "Habilidad manual-precisión", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "k-artesanal", name: "Trabajo artesanal", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "k-experiencia", name: "Experiencia requerida", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "k-actualizacion", name: "Actualización constante", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "k-cobertura", name: "Complejidad en la cobertura el puesto", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
  ],
  responsibility: [
    {
      id: "r-personas", name: "Personas a su cargo", weight: 100, gender: "NEUTRO", selected: true,
      desc: "Número y nivel de personas reportando al puesto",
      levels: [
        { id: "L0", label: "Ninguna", desc: "No tiene personas a su cargo" },
        { id: "L1", label: "1-3 personas", desc: "Equipo pequeño" },
        { id: "L2", label: "4-10 personas", desc: "Equipo mediano" },
        { id: "L3", label: "11-30 personas", desc: "Equipo grande" },
        { id: "L4", label: "Más de 30", desc: "Departamento completo" },
      ],
    },
    { id: "r-resultados", name: "Repercusión en resultados de la organización", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "r-maquinaria", name: "Responsabilidad sobre maquinaria", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "r-seguridad", name: "Seguridad de otras personas", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "r-confidencial", name: "Confidencialidad", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "r-autonomia", name: "Nivel de autonomía y responsabilidad", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
  ],
  effort: [
    {
      id: "e-mental", name: "Esfuerzo mental", weight: 50, gender: "NEUTRO", selected: true,
      desc: "Carga mental, atención y procesamiento requerido",
      levels: [
        { id: "L0", label: "Bajo", desc: "Tareas rutinarias, baja atención" },
        { id: "L1", label: "Medio", desc: "Atención continua, decisiones puntuales" },
        { id: "L2", label: "Alto", desc: "Toma de decisiones complejas" },
        { id: "L3", label: "Muy alto", desc: "Decisiones estratégicas continuas" },
      ],
    },
    {
      id: "e-concentracion", name: "Concentración", weight: 50, gender: "NEUTRO", selected: true,
      desc: "Nivel de concentración mantenida durante la jornada",
      levels: [
        { id: "L0", label: "Baja" },
        { id: "L1", label: "Media" },
        { id: "L2", label: "Alta" },
        { id: "L3", label: "Muy alta" },
      ],
    },
    { id: "e-emocional", name: "Esfuerzo emocional", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "e-fisico", name: "Esfuerzo físico: carga y descarga", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "e-posturas", name: "Posturas forzadas", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "e-repetitivos", name: "Movimientos repetitivos", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "e-pantallas", name: "Visualización de pantallas", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
  ],
  conditions: [
    {
      id: "c-nocturnos", name: "Trabajos nocturnos", weight: 50, gender: "NEUTRO", selected: true,
      desc: "Frecuencia de trabajo en horario nocturno",
      levels: [
        { id: "L0", label: "Nunca" },
        { id: "L1", label: "Ocasionalmente" },
        { id: "L2", label: "Frecuentemente" },
        { id: "L3", label: "Habitualmente" },
      ],
    },
    {
      id: "c-viajes", name: "Viajes", weight: 50, gender: "MASCULINO", selected: true,
      desc: "Frecuencia de desplazamientos por trabajo",
      levels: [
        { id: "L0", label: "Nunca" },
        { id: "L1", label: "Ocasionalmente" },
        { id: "L2", label: "Frecuentemente" },
        { id: "L3", label: "Habitualmente" },
      ],
    },
    { id: "c-nocivos", name: "Manipulación de productos nocivos", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "c-ambientales", name: "Condiciones ambientales", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "c-psicosociales", name: "Condiciones psicosociales", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "c-vial", name: "Riesgo en seguridad vial", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "c-peligrosidad", name: "Peligrosidad del trabajo", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "c-no-laborables", name: "Trabajos días no laborables", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
    { id: "c-dedicacion", name: "Dedicación e incompatibilidad", weight: 0, gender: "NEUTRO", selected: false, levels: [] },
  ],
};

const SCALES = [
  { id: 1, name: "Escala 01", min: 701, max: 999, color: "#16a34a", label: "Directivo Senior" },
  { id: 2, name: "Escala 02", min: 651, max: 700, color: "#eab308", label: "Directivo" },
  { id: 3, name: "Escala 03", min: 601, max: 650, color: "#7c3aed", label: "Mando Superior" },
  { id: 4, name: "Escala 04", min: 551, max: 600, color: "#dc2626", label: "Mando Intermedio" },
  { id: 5, name: "Escala 05", min: 501, max: 550, color: "#0ea5e9", label: "Coord. Senior" },
  { id: 6, name: "Escala 06", min: 451, max: 500, color: "#ec4899", label: "Coordinador" },
  { id: 7, name: "Escala 07", min: 401, max: 450, color: "#f97316", label: "Especialista" },
  { id: 8, name: "Escala 08", min: 351, max: 400, color: "#1e3a8a", label: "Técnico Senior" },
  { id: 9, name: "Escala 09", min: 300, max: 350, color: "#06b6d4", label: "Técnico" },
  { id: 10, name: "Escala 10", min: 251, max: 300, color: "#059669", label: "Operativo Senior" },
  { id: 11, name: "Escala 11", min: 201, max: 250, color: "#a16207", label: "Operativo" },
  { id: 12, name: "Escala 12", min: 151, max: 200, color: "#6b7280", label: "Auxiliar" },
  { id: 13, name: "Escala 13", min: 0,   max: 150, color: "#9ca3af", label: "Aprendiz" },
];

const DEPARTMENTS = [
  { id: 1, name: "Recursos Humanos", description: "Departamento de Recursos Humanos", createdAt: "08/09/2021 12:03" },
  { id: 2, name: "Comercial y Marketing", description: "Departamento Comercial y Marketing", createdAt: "10/09/2021 11:13" },
  { id: 3, name: "Técnico", description: "Departamento Técnico", createdAt: "21/09/2021 11:44" },
  { id: 10, name: "Logística", description: "Departamento de Logística y Distribución", createdAt: "15/02/2022 10:21" },
];

const LEVELS = [
  { id: 1, name: "Personal Directivo", description: "Alta dirección y dirección general", order: 1 },
  { id: 2, name: "Mandos intermedios", description: "Jefatura de área o departamento", order: 2 },
  { id: 3, name: "Personal Técnico", description: "Especialistas y técnicos cualificados", order: 3 },
  { id: 4, name: "Personal Administrativo", description: "Apoyo administrativo y operativo", order: 4 },
  { id: 5, name: "Personal Operativo", description: "Personal de planta y operaciones", order: 5 },
];

const POSITIONS = [
  { id: 1, code: "P01", name: "Director/a Recursos Humanos", departmentId: 1, levelId: 1, points: 585, status: "FINALIZADA", createdAt: "08/09/2021 12:04" },
  { id: 2, code: "P02", name: "Director/a Comercial", departmentId: 2, levelId: 1, points: 0, status: "PENDIENTE", createdAt: "10/09/2021 11:14" },
  { id: 3, code: "P03", name: "Administrativo/a", departmentId: 1, levelId: 4, points: 0, status: "PENDIENTE", createdAt: "21/09/2021 11:45" },
  { id: 4, code: "P04", name: "Jefe/a Almacén", departmentId: 10, levelId: 2, points: 0, status: "PENDIENTE", createdAt: "22/09/2021 10:33" },
  { id: 5, code: "P05", name: "Técnico/a Soporte", departmentId: 3, levelId: 3, points: 412, status: "FINALIZADA", createdAt: "12/10/2021 09:22" },
  { id: 6, code: "P06", name: "Responsable Marketing Digital", departmentId: 2, levelId: 2, points: 478, status: "FINALIZADA", createdAt: "03/11/2021 14:10" },
  { id: 7, code: "P07", name: "Comercial Senior", departmentId: 2, levelId: 3, points: 380, status: "FINALIZADA", createdAt: "12/01/2022 10:00" },
  { id: 8, code: "P08", name: "Mozo de Almacén", departmentId: 10, levelId: 5, points: 215, status: "FINALIZADA", createdAt: "14/03/2022 16:40" },
  { id: 9, code: "P09", name: "Técnico/a de Sistemas", departmentId: 3, levelId: 3, points: 425, status: "FINALIZADA", createdAt: "20/05/2022 11:00" },
  { id: 1318, code: "P01318", name: "Conductor/a", departmentId: 10, levelId: 4, points: 0, status: "PENDIENTE", createdAt: "20/10/2023 12:44" },
];

const ENTITY = {
  name: "Industrias Aurea S.L.",
  cif: "B-86543210",
  sector: "Industrial y Manufactura",
  size: 142,
  founded: 1998,
  address: "C/ Gran Vía 28, 28013 Madrid",
  phone: "+34 910 234 567",
  email: "rrhh@aurea.es",
  web: "www.aurea.es",
  convenio: "Industria Siderometalúrgica de Madrid",
  description: "Empresa industrial dedicada a la fabricación de componentes metálicos de precisión para los sectores aeronáutico, automoción y energético. Con presencia en España y exportación a 14 países europeos.",
};

/* Pre-seeded valuation for P01 — Director/a RRHH (Escala 04, 585 pts) */

const ROLES = [
  {
    id: 1,
    name: "Administrador",
    description: "Acceso completo. Configura factores, escalas, valida y exporta.",
    color: "#0a84ff",
    permissions: {
      dashboard: { view: true },
      entity: { view: true, edit: true },
      departments: { view: true, edit: true },
      levels: { view: true, edit: true },
      positions: { view: true, edit: true },
      factors: { view: true, edit: true },
      scales: { view: true, edit: true },
      valuation: { view: true, edit: true, finalize: true },
      users: { view: true, edit: true },
      roles: { view: true, edit: true },
    },
    builtIn: true,
  },
  {
    id: 2,
    name: "Responsable RRHH",
    description: "Gestiona valoraciones y consulta el organigrama. No modifica el sistema.",
    color: "#34c759",
    permissions: {
      dashboard: { view: true },
      entity: { view: true, edit: false },
      departments: { view: true, edit: true },
      levels: { view: true, edit: false },
      positions: { view: true, edit: true },
      factors: { view: true, edit: false },
      scales: { view: true, edit: false },
      valuation: { view: true, edit: true, finalize: true },
      users: { view: false, edit: false },
      roles: { view: false, edit: false },
    },
  },
  {
    id: 3,
    name: "Valorador",
    description: "Realiza valoraciones de puestos sin finalizar. Requiere aprobación.",
    color: "#af52de",
    permissions: {
      dashboard: { view: true },
      entity: { view: true, edit: false },
      departments: { view: true, edit: false },
      levels: { view: true, edit: false },
      positions: { view: true, edit: false },
      factors: { view: true, edit: false },
      scales: { view: true, edit: false },
      valuation: { view: true, edit: true, finalize: false },
      users: { view: false, edit: false },
      roles: { view: false, edit: false },
    },
  },
  {
    id: 4,
    name: "Auditor",
    description: "Solo lectura sobre todo el sistema. Pensado para inspectores y consultores.",
    color: "#ff9500",
    permissions: {
      dashboard: { view: true },
      entity: { view: true, edit: false },
      departments: { view: true, edit: false },
      levels: { view: true, edit: false },
      positions: { view: true, edit: false },
      factors: { view: true, edit: false },
      scales: { view: true, edit: false },
      valuation: { view: true, edit: false, finalize: false },
      users: { view: false, edit: false },
      roles: { view: false, edit: false },
    },
  },
];

const USERS = [
  { id: 1, name: "José Luis Martín",   email: "jl.martin@aurea.es",       roleId: 1, departmentId: 1, status: "ACTIVO",    lastLogin: "hace 5 min",    createdAt: "08/09/2021" },
  { id: 2, name: "Marta Sánchez Ruiz", email: "m.sanchez@aurea.es",       roleId: 2, departmentId: 1, status: "ACTIVO",    lastLogin: "hace 1 h",      createdAt: "12/09/2021" },
  { id: 3, name: "Daniel Pérez",       email: "d.perez@aurea.es",         roleId: 3, departmentId: 2, status: "ACTIVO",    lastLogin: "ayer",          createdAt: "20/01/2022" },
  { id: 4, name: "Lucía Fernández",    email: "l.fernandez@aurea.es",     roleId: 3, departmentId: 3, status: "ACTIVO",    lastLogin: "hace 3 días",   createdAt: "14/03/2022" },
  { id: 5, name: "Ana García López",   email: "a.garcia@aurea.es",        roleId: 2, departmentId: 1, status: "INVITADO",  lastLogin: "—",             createdAt: "22/05/2026" },
  { id: 6, name: "Roberto Castillo",   email: "r.castillo@auditoria.es",  roleId: 4, departmentId: null, status: "ACTIVO", lastLogin: "hace 2 sem",    createdAt: "10/10/2023", external: true },
  { id: 7, name: "Pablo Méndez",       email: "p.mendez@aurea.es",        roleId: 3, departmentId: 10, status: "SUSPENDIDO", lastLogin: "hace 1 mes", createdAt: "07/06/2024" },
];

/* Pre-seeded valuation for P01 — Director/a RRHH (Escala 04, 585 pts) */
const SEED_VALUATIONS = {
  1: { // position id
    "k-formacion": "L5",   // Licenciatura
    "k-idiomas": "L3",     // Alto
    "k-liderazgo": "L3",   // Alto
    "k-iniciativa": "L3",  // Alta
    "k-maquinaria": "L0",
    "r-personas": "L3",    // 11-30
    "e-mental": "L3",
    "e-concentracion": "L2",
    "c-nocturnos": "L0",
    "c-viajes": "L1",
  },
  5: {
    "k-formacion": "L3", "k-idiomas": "L2", "k-liderazgo": "L1", "k-iniciativa": "L2", "k-maquinaria": "L2",
    "r-personas": "L0", "e-mental": "L2", "e-concentracion": "L2", "c-nocturnos": "L1", "c-viajes": "L0",
  },
  6: {
    "k-formacion": "L4", "k-idiomas": "L3", "k-liderazgo": "L2", "k-iniciativa": "L3", "k-maquinaria": "L0",
    "r-personas": "L1", "e-mental": "L3", "e-concentracion": "L2", "c-nocturnos": "L0", "c-viajes": "L1",
  },
  7: {
    "k-formacion": "L3", "k-idiomas": "L2", "k-liderazgo": "L1", "k-iniciativa": "L2", "k-maquinaria": "L0",
    "r-personas": "L0", "e-mental": "L2", "e-concentracion": "L2", "c-nocturnos": "L0", "c-viajes": "L2",
  },
  8: {
    "k-formacion": "L1", "k-idiomas": "L0", "k-liderazgo": "L0", "k-iniciativa": "L0", "k-maquinaria": "L1",
    "r-personas": "L0", "e-mental": "L0", "e-concentracion": "L1", "c-nocturnos": "L1", "c-viajes": "L0",
  },
  9: {
    "k-formacion": "L4", "k-idiomas": "L3", "k-liderazgo": "L1", "k-iniciativa": "L2", "k-maquinaria": "L1",
    "r-personas": "L0", "e-mental": "L2", "e-concentracion": "L2", "c-nocturnos": "L1", "c-viajes": "L0",
  },
};

/* Compute SVPT total for a single position given its valuations.
   Formula:
     totalPoints = round(
       sum over groups(
         (group.svpt/100) * sum over selected items(
           item.weight * (levelIndex/(maxLevels-1)) ) ) * 10 )
   With current weights (sum within group ~100) and group SVPT summing 100,
   max result is 1000.
*/
function computePositionPoints(valuationByItem, factorsState) {
  if (!valuationByItem) return 0;
  let total = 0;
  for (const grp of factorsState.groups) {
    const items = factorsState.items[grp.id] || [];
    const selected = items.filter(it => it.selected && it.levels.length > 0);
    const groupTotalWeight = selected.reduce((s, it) => s + (it.weight || 0), 0) || 1;
    let groupScore = 0;
    for (const it of selected) {
      const chosen = valuationByItem[it.id];
      if (!chosen) continue;
      const idx = it.levels.findIndex(l => l.id === chosen);
      if (idx < 0) continue;
      const maxIdx = it.levels.length - 1;
      const ratio = maxIdx === 0 ? 0 : idx / maxIdx;
      // normalize item weight by group total to be safe even if not exactly 100
      const itemWeightRatio = it.weight / groupTotalWeight;
      groupScore += itemWeightRatio * ratio;
    }
    total += (grp.svpt / 100) * groupScore;
  }
  return Math.round(total * 1000);
}

function findScaleForPoints(points, scales) {
  return scales.find(s => points >= s.min && points <= s.max) || null;
}

function countCompletedItems(valuationByItem, factorsState) {
  if (!valuationByItem) return 0;
  let total = 0, done = 0;
  for (const grp of factorsState.groups) {
    const items = factorsState.items[grp.id] || [];
    for (const it of items) {
      if (it.selected && it.levels.length > 0) {
        total++;
        if (valuationByItem[it.id]) done++;
      }
    }
  }
  return { total, done };
}

/* Build initial state */
export const TALENTION_INITIAL = {
  entity: ENTITY,
  departments: DEPARTMENTS,
  levels: LEVELS,
  positions: POSITIONS,
  factors: {
    groups: FACTOR_GROUPS.map(g => ({ ...g, svpt: g.defaultSVPT })),
    items: FACTOR_ITEMS,
  },
  scales: SCALES,
  valuations: SEED_VALUATIONS,
  users: USERS,
  roles: ROLES,
};

export const TALENTION_HELPERS = {
  computePositionPoints,
  findScaleForPoints,
  countCompletedItems,
  FACTOR_GROUPS,
};
